import * as THREE from 'three';
import { Inventory } from './inventory';

// Death bodies. When you die your inventory is left ON your corpse where you fell. Anyone (you or another
// player) can walk up and LOOT it (E) for the carried resources, or CHOP it up (hit it) for bone + body
// fat. A body persists until it is chopped to nothing or 7 real days pass (server-time placeholder until
// persistence lands; client-only for now, so the timer effectively only matters once the world is saved).

const LIFETIME_MS = 7 * 24 * 60 * 60 * 1000; // 7 real (24h) days
const CHOPS = 6;       // hits to fully harvest a body
const BONE_PER_CHOP = 4;
const FAT_PER_CHOP = 3;

export class Corpse {
  readonly group = new THREE.Group();
  readonly pos: THREE.Vector3;
  readonly contents: Inventory; // the dead player's carried resources (loot via E)
  readonly bornMs: number;
  id = 0; // stable wire id, set by the manager on spawn (spec section 19)
  chopsLeft = CHOPS;

  constructor(pos: THREE.Vector3, contents: Inventory, nowMs: number, yaw: number) {
    this.pos = pos.clone();
    this.contents = contents;
    this.bornMs = nowMs;
    buildCorpseMesh(this.group);
    this.group.position.copy(this.pos);
    this.group.rotation.y = yaw;
    this.group.traverse((o) => { o.userData.corpse = this; });
  }
}

export class CorpseManager {
  readonly group = new THREE.Group();
  private readonly list: Corpse[] = [];
  private nextId = 1;

  spawn(pos: THREE.Vector3, contents: Inventory, nowMs: number, yaw: number): Corpse {
    const c = new Corpse(pos, contents, nowMs, yaw);
    c.id = this.nextId++;
    this.list.push(c);
    this.group.add(c.group);
    return c;
  }

  // Chop a body by its stable id (so the action can come over the wire). Null if the body is already gone.
  chopById(id: number): { bone: number; fat: number; gone: boolean; pos: THREE.Vector3 } | null {
    const c = this.list.find((x) => x.id === id);
    return c ? this.chop(c) : null;
  }

  // Where a body lies by its stable id (for the network relay), or null.
  posById(id: number): THREE.Vector3 | null {
    const c = this.list.find((x) => x.id === id);
    return c ? c.pos : null;
  }

  // Closest body within horizontal range (for the E loot prompt), or null.
  nearest(p: THREE.Vector3, range: number): Corpse | null {
    let best: Corpse | null = null;
    let bestSq = range * range;
    for (const c of this.list) {
      const dx = c.pos.x - p.x, dz = c.pos.z - p.z, sq = dx * dx + dz * dz;
      if (sq <= bestSq) { bestSq = sq; best = c; }
    }
    return best;
  }

  // The body under the cursor ray (for chop-aim), or null.
  raycast(raycaster: THREE.Raycaster): Corpse | null {
    const hits = raycaster.intersectObject(this.group, true);
    for (const h of hits) {
      let o: THREE.Object3D | null = h.object;
      while (o) { if (o.userData.corpse) return o.userData.corpse as Corpse; o = o.parent; }
    }
    return null;
  }

  // Chop a body once: yields bone + fat; the body is consumed after CHOPS hits (un-looted contents are
  // then lost - looting and chopping are both allowed, so loot first if you want the gear too).
  chop(c: Corpse): { bone: number; fat: number; gone: boolean; pos: THREE.Vector3 } {
    c.chopsLeft -= 1;
    const gone = c.chopsLeft <= 0;
    const pos = c.pos.clone();
    if (gone) this.remove(c);
    return { bone: BONE_PER_CHOP, fat: FAT_PER_CHOP, gone, pos };
  }

  remove(c: Corpse): void {
    const i = this.list.indexOf(c);
    if (i < 0) return;
    this.list.splice(i, 1);
    this.group.remove(c.group);
  }

  // Expire bodies older than the lifetime (call each frame with the wall clock).
  update(nowMs: number): void {
    for (let i = this.list.length - 1; i >= 0; i--) {
      if (nowMs - this.list[i].bornMs > LIFETIME_MS) this.remove(this.list[i]);
    }
  }
}

function buildCorpseMesh(g: THREE.Group): void {
  const skin = new THREE.MeshLambertMaterial({ color: 0xb78a5e, flatShading: true });
  const shirt = new THREE.MeshLambertMaterial({ color: 0x4a5663, flatShading: true });
  const pants = new THREE.MeshLambertMaterial({ color: 0x333c49, flatShading: true });
  // A body lying on its back, low to the ground: torso, head (+z), arms out to the sides, legs (-z).
  const parts: Array<[THREE.BoxGeometry, THREE.Material, [number, number, number]]> = [
    [new THREE.BoxGeometry(0.5, 0.26, 0.62), shirt, [0, 0.13, 0]],
    [new THREE.BoxGeometry(0.28, 0.26, 0.28), skin, [0, 0.14, 0.46]],
    [new THREE.BoxGeometry(0.15, 0.16, 0.5), skin, [-0.36, 0.1, 0.06]],
    [new THREE.BoxGeometry(0.15, 0.16, 0.5), skin, [0.36, 0.1, 0.06]],
    [new THREE.BoxGeometry(0.18, 0.18, 0.6), pants, [-0.13, 0.1, -0.56]],
    [new THREE.BoxGeometry(0.18, 0.18, 0.6), pants, [0.13, 0.1, -0.56]],
  ];
  for (const [geo, mat, p] of parts) {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(p[0], p[1], p[2]);
    m.castShadow = true;
    m.receiveShadow = true;
    g.add(m);
  }
}
